/**
 * Domain signature-block catalog — categories + packs.
 * Size (compact / standard / spacious) comes from SIGN_PDF_BLOCK_PRESETS.
 */

export const SIGN_PDF_BLOCK_LIBRARY_FEATURE = "sign_pdf_block_library";

/** Encode domain + size for drag MIME / place handlers. */
export function encodeSignPdfBlockPayload(domainId, sizeId = "standard") {
  return `${domainId}:${sizeId}`;
}

/** Parse `domainId:sizeId` or legacy bare size id. */
export function parseSignPdfBlockPayload(raw) {
  const value = String(raw || "").trim();
  if (!value) {
    return { domainId: "classic", sizeId: "standard" };
  }
  if (value.includes(":")) {
    const [domainId, sizeId] = value.split(":");
    return {
      domainId: domainId || "classic",
      sizeId: sizeId || "standard",
    };
  }
  // Legacy: size-only MIME from the old palette chips
  if (value === "compact" || value === "standard" || value === "spacious") {
    return { domainId: "classic", sizeId: value };
  }
  return { domainId: value, sizeId: "standard" };
}

/**
 * Catalog of placeable signing blocks by industry / domain.
 * `pro: false` = free; others gate behind sign_pdf_block_library.
 */
export const SIGN_PDF_BLOCK_CATEGORIES = [
  {
    id: "general",
    label: "General",
    blocks: [
      {
        id: "classic",
        label: "Classic",
        description: "Right · name + sig + date",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: false,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "standard",
      },
      {
        id: "classic_left",
        label: "Left align",
        description: "Left · name + sig + date",
        preview: "name-sig-date",
        previewAlign: "left",
        pro: false,
        includeName: true,
        includeDate: true,
        position: "bottom-left",
        defaultSizeId: "standard",
      },
      {
        id: "sig_date",
        label: "Signature + date",
        description: "No name line — tight stack",
        preview: "sig-date",
        previewAlign: "right",
        pro: false,
        includeName: false,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "compact",
      },
      {
        id: "centered",
        label: "Centered",
        description: "Bottom center stack",
        preview: "name-sig-date",
        previewAlign: "center",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom",
        defaultSizeId: "standard",
      },
    ],
  },
  {
    id: "hr",
    label: "HR",
    blocks: [
      {
        id: "hr_offer",
        label: "Offer letter",
        description: "Employee offer · spacious",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "spacious",
      },
      {
        id: "hr_onboarding",
        label: "Onboarding",
        description: "Policy acknowledgement",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: false,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "standard",
      },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    blocks: [
      {
        id: "sales_quote",
        label: "Quote / proposal",
        description: "Client acceptance block",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "standard",
      },
      {
        id: "sales_order",
        label: "Sales order",
        description: "Signature + date · compact",
        preview: "sig-date",
        previewAlign: "right",
        pro: false,
        includeName: false,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "compact",
      },
    ],
  },
  {
    id: "education",
    label: "Education",
    blocks: [
      {
        id: "edu_consent",
        label: "Consent form",
        description: "Parent / guardian stack",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "standard",
      },
      {
        id: "edu_enrollment",
        label: "Enrollment",
        description: "Left-aligned acknowledgement",
        preview: "name-sig-date",
        previewAlign: "left",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-left",
        defaultSizeId: "standard",
      },
    ],
  },
  {
    id: "legal",
    label: "Legal",
    blocks: [
      {
        id: "legal_contract",
        label: "Contract",
        description: "Full stack · bottom right",
        preview: "name-sig-date",
        previewAlign: "right",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-right",
        defaultSizeId: "standard",
      },
      {
        id: "legal_nda",
        label: "NDA",
        description: "Full stack · bottom left",
        preview: "name-sig-date",
        previewAlign: "left",
        pro: true,
        includeName: true,
        includeDate: true,
        position: "bottom-left",
        defaultSizeId: "standard",
      },
    ],
  },
];

export function listSignPdfDomainBlocks() {
  return SIGN_PDF_BLOCK_CATEGORIES.flatMap((cat) =>
    (cat.blocks || []).map((block) => ({
      ...block,
      categoryId: cat.id,
      categoryLabel: cat.label,
    }))
  );
}

export function getSignPdfDomainBlock(domainId) {
  return (
    listSignPdfDomainBlocks().find((b) => b.id === domainId) ||
    listSignPdfDomainBlocks().find((b) => b.id === "classic") ||
    null
  );
}
